/**
 * Image compression utility using Canvas API
 * Compresses images to reduce file size before upload
 */

export interface CompressionOptions {
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  quality?: number;
}

/**
 * Compresses an image data URL to reduce file size
 * @param dataUrl - The image data URL to compress
 * @param options - Compression options
 * @returns Promise resolving to compressed image data URL
 */
export const compressImage = async (
  dataUrl: string,
  options: CompressionOptions = {}
): Promise<string> => {
  const {
    maxSizeMB = 2,
    maxWidthOrHeight = 1920,
    quality = 0.8,
  } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      try {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;

        if (width > maxWidthOrHeight || height > maxWidthOrHeight) {
          if (width > height) {
            height = (height / width) * maxWidthOrHeight;
            width = maxWidthOrHeight;
          } else {
            width = (width / height) * maxWidthOrHeight;
            height = maxWidthOrHeight;
          }
        }

        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // Use better image smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to data URL with compression
        const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);

        // Check if compressed size is acceptable
        const sizeInMB = (compressedDataUrl.length * 0.75) / (1024 * 1024);

        if (sizeInMB > maxSizeMB && quality > 0.1) {
          // Recursively compress with lower quality if still too large
          compressImage(dataUrl, { ...options, quality: quality - 0.1 })
            .then(resolve)
            .catch(reject);
        } else {
          resolve(compressedDataUrl);
        }
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image for compression'));
    };

    img.src = dataUrl;
  });
};

/**
 * Gets the size of a data URL in MB
 * @param dataUrl - The data URL to measure
 * @returns Size in MB
 */
export const getDataUrlSizeInMB = (dataUrl: string): number => {
  // Base64 encoded data is ~33% larger than the actual data
  // Subtract the data URL prefix to get actual base64 length
  const base64Length = dataUrl.split(',')[1]?.length || 0;
  return (base64Length * 0.75) / (1024 * 1024);
};

/**
 * Validates if an image data URL is within size limits
 * @param dataUrl - The data URL to validate
 * @param maxSizeMB - Maximum allowed size in MB
 * @returns true if valid, false otherwise
 */
export const isImageWithinSizeLimit = (dataUrl: string, maxSizeMB: number): boolean => {
  const sizeMB = getDataUrlSizeInMB(dataUrl);
  return sizeMB <= maxSizeMB;
};
